import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import AdminVideoFileField from '@/components/admin/videoCreate/AdminVideoFileField';
import {
  createVideoMaterialUploadTarget,
  fileUploadMeta,
  getAdminVideoMaterials,
  putFileToPresignedUrl,
  setAdminVideoMaterials,
  toAdminApiErrorMessage,
} from '@/api/adminVideos';
import {
  MATERIAL_ACCEPT,
  MAX_MATERIAL_BYTES,
  MAX_QUESTIONNAIRE_URL_LENGTH,
  formatBytes,
  validateMaterialFile,
  validateQuestionnaireUrl,
} from '@/config/adminVideoUpload';

/**
 * 動画1本の配布資料・アンケートURLを変更するモーダル。
 *
 * 一覧（`view=card`）は資料もアンケートURLも返さないため、開いたときに現在値を取り直す。
 * 資料は新規追加と同じく署名付きURLでS3へ直接送り、確定（`study/` へのコピーとDB更新）は
 * Lambda 側に任せる。ファイルを選ばなかった場合は資料に触らず、アンケートURLだけを更新する。
 */
export default function AdminVideoMaterialEditDialog({ video, open, onOpenChange }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const videoId = video?.id ?? null;

  const [materialFile, setMaterialFile] = useState(null);
  const [questionnaireUrlDraft, setQuestionnaireUrlDraft] = useState('');
  const [isSeeded, setIsSeeded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const currentQuery = useQuery({
    queryKey: ['admin-video-materials', videoId],
    queryFn: () => getAdminVideoMaterials(videoId),
    enabled: Boolean(open && videoId),
    // 入力途中に裏で取り直して、書きかけのURLを巻き戻さないようにする
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // 現在のアンケートURLを入力欄の初期値にするのは、開いてから1回だけ
  useEffect(() => {
    if (!open || isSeeded || !currentQuery.data) return;
    setQuestionnaireUrlDraft(currentQuery.data.questionnaireUrl ?? '');
    setIsSeeded(true);
  }, [open, isSeeded, currentQuery.data]);

  useEffect(() => {
    if (open) return;
    setIsSeeded(false);
    setMaterialFile(null);
    setQuestionnaireUrlDraft('');
    setFieldErrors({});
    setErrorMessage(null);
    setUploadProgress(0);
  }, [open]);

  const saveMutation = useMutation({
    mutationFn: async ({ file, questionnaireUrl }) => {
      let uploadId = null;
      let materialObjectKey = null;

      if (file) {
        const issued = await createVideoMaterialUploadTarget(fileUploadMeta(file));
        const target = issued?.target;
        if (!target?.uploadUrl) throw new Error('upload_target_missing');

        await putFileToPresignedUrl({
          url: target.uploadUrl,
          file,
          contentType: target.contentType,
          onProgress: setUploadProgress,
        });
        setUploadProgress(1);

        uploadId = issued.uploadId;
        materialObjectKey = target.objectKey;
      }

      return setAdminVideoMaterials(videoId, { questionnaireUrl, uploadId, materialObjectKey });
    },
    onSuccess: async (result) => {
      setErrorMessage(null);
      // 再生ページが参照するキャッシュも落として、次に開いたときに新しい値が出るようにする
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-videos'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-video-materials', videoId] }),
        queryClient.invalidateQueries({ queryKey: ['video-detail'] }),
      ]);
      toast({
        description: result.materials.length
          ? '配布資料・アンケートURLを変更しました'
          : 'アンケートURLを変更しました',
      });
      onOpenChange(false);
    },
    onError: (error) => {
      // 入力を捨てないよう、モーダルは開いたままにする
      const message = toAdminApiErrorMessage(
        error,
        '配布資料・アンケートURLの変更に失敗しました。'
      );
      setErrorMessage(message);
      setUploadProgress(0);
      toast({ variant: 'destructive', description: message });
    },
  });

  const handleFileChange = (file) => {
    setFieldErrors((current) => ({ ...current, materialFile: undefined }));
    setErrorMessage(null);
    setMaterialFile(file);
  };

  const handleSave = () => {
    if (saveMutation.isPending) return;

    const errors = {};
    const materialError = validateMaterialFile(materialFile);
    if (materialError) errors.materialFile = materialError;
    const questionnaireUrlError = validateQuestionnaireUrl(questionnaireUrlDraft);
    if (questionnaireUrlError) errors.questionnaireUrl = questionnaireUrlError;

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setErrorMessage(null);
    setUploadProgress(0);
    saveMutation.mutate({
      file: materialFile,
      questionnaireUrl: questionnaireUrlDraft.trim(),
    });
  };

  const handleOpenChange = (nextOpen) => {
    // 保存中に閉じられると結果が分からなくなるため待たせる
    if (saveMutation.isPending) return;
    onOpenChange(nextOpen);
  };

  const currentMaterials = currentQuery.data?.materials ?? [];
  const savedQuestionnaireUrl = currentQuery.data?.questionnaireUrl ?? '';
  const hasChanges =
    Boolean(materialFile) || questionnaireUrlDraft.trim() !== savedQuestionnaireUrl.trim();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg border-[#e8dccd] bg-[#faf6f1]">
        <DialogHeader>
          <DialogTitle className="text-[#2d1810]">資料・アンケートURL変更</DialogTitle>
          <DialogDescription className="text-[#6b5540]">
            {video?.title || '(タイトル未設定)'}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <div
            role="alert"
            className="rounded-xl border border-[#d98a7a] bg-[#fdf1ee] px-4 py-3 text-sm text-[#8a3b28]"
          >
            {errorMessage}
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-medium text-[#2d1810]">現在の配布資料</p>

          {currentQuery.isLoading ? (
            <p className="text-sm text-[#6b5540]">読み込み中...</p>
          ) : currentQuery.isError ? (
            <p className="text-sm text-[#8a3b28]">
              {toAdminApiErrorMessage(currentQuery.error, '現在の設定を取得できませんでした。')}
            </p>
          ) : currentMaterials.length === 0 ? (
            <p className="text-sm text-[#6b5540]">配布資料は登録されていません。</p>
          ) : (
            <ul className="space-y-1">
              {currentMaterials.map((material) => (
                <li key={material.url}>
                  <a
                    href={material.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 break-all text-sm text-[#5d4529] underline transition-colors hover:text-[#2d1810]"
                  >
                    {material.name}
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-[#8b6f47]" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <AdminVideoFileField
          id="admin-video-material-file"
          label="新しい配布資料"
          accept={MATERIAL_ACCEPT}
          file={materialFile}
          disabled={saveMutation.isPending || currentQuery.isLoading}
          error={fieldErrors.materialFile}
          hint={`選ばなければ現在の資料のままです（最大${formatBytes(MAX_MATERIAL_BYTES)}）`}
          onChange={handleFileChange}
        />

        <div>
          <Label
            htmlFor="admin-video-questionnaire-url"
            className="mb-2 block text-sm font-medium text-[#2d1810]"
          >
            アンケートURL
            <span className="ml-1 text-xs font-normal text-[#b4462f]">必須</span>
          </Label>
          <Input
            id="admin-video-questionnaire-url"
            type="url"
            inputMode="url"
            value={questionnaireUrlDraft}
            onChange={(event) => {
              setFieldErrors((current) => ({ ...current, questionnaireUrl: undefined }));
              setQuestionnaireUrlDraft(event.target.value);
            }}
            placeholder="https://example.com/forms/..."
            maxLength={MAX_QUESTIONNAIRE_URL_LENGTH}
            disabled={saveMutation.isPending || currentQuery.isLoading}
            className="bg-white text-[#2d1810]"
          />
          {fieldErrors.questionnaireUrl ? (
            <p className="mt-1 text-xs text-[#b4462f]">{fieldErrors.questionnaireUrl}</p>
          ) : (
            <p className="mt-1 text-xs text-[#8b6f47]">
              {savedQuestionnaireUrl
                ? '現在登録されているURLを表示しています。'
                : 'この動画にはまだアンケートURLが登録されていません。'}
            </p>
          )}
        </div>

        {saveMutation.isPending && materialFile && (
          <p className="text-xs text-[#6b5540]">
            アップロード中... {Math.round(uploadProgress * 100)}%
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={saveMutation.isPending}
            className="border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
          >
            キャンセル
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isPending || currentQuery.isLoading || !hasChanges}
            className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#7a6040]"
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
